import React, { useMemo, useState } from 'react';
import {
  BackButton,
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { TripMatchStatusUpdate, TripMatch } from '@marageti/z4-sdk/lib/travel';
import {
  Container,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { SubmitHandler, FieldValues } from 'react-hook-form';
import MatchCancelForm from './match-cancel-form';
import { statusByReason, filterOptionsByTMStatus } from './trip-request-edit-form-util';
import { isDeclinedOrCanceled } from '../../../../utils/trip-matches';

const MatchCancelContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { tripRequestId, tripMatchId } = useParams();

  const {
    data, mutate, error, isLoading,
  } = useFetch<TripMatch[]>(apiClient.tripRequestsClient.getTripRequestTripMatches, [tripRequestId], true);
  const tripMatch = data?.find((tm) => tm.id === tripMatchId);

  const [submissionError, setSubmissionError] = useState<ApiError>();
  const reasonOptions = useMemo(() => {
    if (tripMatch) {
      return filterOptionsByTMStatus(tripMatch.status);
    }
    return [];
  }, [tripMatch]);

  const onSubmit: SubmitHandler<FieldValues> = (formValues) => {
    if (tripMatchId) {
      const statusUpdate: TripMatchStatusUpdate = {
        id: tripMatchId,
        status: statusByReason[formValues.reason],
        statusInfo: {
          reason: `${formValues.reason}: ${formValues.comments}`,
        },
      };
      apiClient.tripMatchesClient.putTripMatchStatus(tripMatchId!, statusUpdate)
        .then(() => {
          mutate();
          enqueueSnackbar('Match canceled', { variant: 'success' });
          navigate(`/trip-requests/${tripRequestId}/matches`);
        })
        .catch((e: ApiError) => {
          if (e?.code === 401) navigate('/logout');
          setSubmissionError(e);
        });
    }
  };

  const onCancel = () => navigate(`/trip-requests/${tripRequestId}/matches`);

  if (error?.code === 401) return <Navigate to="/logout" />;
  if (!isLoading && (!tripMatch || isDeclinedOrCanceled(tripMatch.status))) return <Navigate to={`/trip-requests/${tripRequestId}/matches`} />;

  return (
    <Container maxWidth="sm">
      <Stack spacing={3} sx={{ pt: 6, pb: 7, alignItems: 'flex-start' }}>
        <BackButton
          aria-label="back to trip"
          defaultPath={`/trip-requests/${tripRequestId}`}
        />
        <Stack spacing={1.5}>
          <Typography variant="h2">
            Cancel Match
          </Typography>
          <Typography color="primary.light">
            Please select the reason for canceling this trip match and provide additional details in the comments section.
          </Typography>
        </Stack>
        {isLoading ? <Skeleton height={310} width="100%" /> : (
          <MatchCancelForm
            onCancel={onCancel}
            onSubmit={onSubmit}
            reasonOptions={reasonOptions}
            submissionError={submissionError}
          />
        )}
      </Stack>
    </Container>

  );
};

export default MatchCancelContainer;
